import React from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import styles from "./Charts.module.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface LineChartProps {
  title: string;
  data: Array<{ date: string; value: number }>;
  multiLine?: {
    datasets: Array<{
      label: string;
      data: number[];
      color: string;
    }>;
    labels: string[];
  };
  subtitle?: React.ReactNode;
  currency?: string; // Currency symbol to display with values
  color?: string; // Line color for single line charts
}

const LineChart: React.FC<LineChartProps> = ({
  title,
  data,
  multiLine,
  subtitle,
  currency,
  color = "#4A90E2",
}) => {
  // Optimize for large datasets - hide points if more than 100 data points
  const isLargeDataset = multiLine
    ? multiLine.labels.length > 100
    : data.length > 100;
  const pointRadius = isLargeDataset ? 0 : 5;
  const pointHoverRadius = isLargeDataset ? 3 : 7;

  const chartData = multiLine
    ? {
        labels: multiLine.labels,
        datasets: multiLine.datasets.map((dataset) => ({
          label: dataset.label,
          data: dataset.data,
          borderColor: dataset.color,
          backgroundColor: dataset.color,
          tension: 0.4,
          fill: false,
          pointRadius,
          pointHoverRadius,
          pointBackgroundColor: dataset.color,
          pointBorderColor: "#fff",
          pointBorderWidth: 2,
          pointHoverBorderWidth: 2,
          borderWidth: 3,
        })),
      }
    : {
        labels: data.map((d) => d.date),
        datasets: [
          {
            label: title,
            data: data.map((d) => d.value),
            borderColor: color,
            backgroundColor: color,
            tension: 0.4,
            fill: false,
            pointRadius,
            pointHoverRadius,
            pointBackgroundColor: color,
            pointBorderColor: "#fff",
            pointBorderWidth: 2,
            pointHoverBorderWidth: 2,
            borderWidth: 3,
          },
        ],
      };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: !!multiLine,
        position: "top" as const,
        align: "start" as const,
        labels: {
          usePointStyle: true,
          pointStyle: "rect" as const,
          padding: 15,
          font: {
            family: "Inter, sans-serif",
            size: 13,
          },
          boxWidth: 24,
          boxHeight: 24,
          color: "#000",
        },
        padding: {
          bottom: 16,
        },
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
        backgroundColor: "rgba(30, 58, 95, 0.95)",
        padding: 12,
        titleFont: {
          size: 13,
          weight: "bold" as const,
        },
        bodyFont: {
          size: 12,
        },
        borderColor: "#E8E9EB",
        borderWidth: 1,
        displayColors: true,
        boxPadding: 6,
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.y;
            const formattedValue = value.toLocaleString("en-US", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            });
            return currency
              ? `${context.dataset.label}: ${currency} ${formattedValue}`
              : `${context.dataset.label}: ${formattedValue}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "#F5F5F7",
          lineWidth: 1,
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 12,
          },
          color: "#999",
          padding: 8,
          callback: (value: any) => {
            const formattedValue = Number(value).toLocaleString("en-US", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            });
            return currency ? `${currency} ${formattedValue}` : formattedValue;
          },
        },
        border: {
          display: false,
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          font: {
            size: 12,
          },
          color: "#999",
          padding: 8,
          align: "end" as const,
          // For large datasets (>100), limit labels to avoid crowding
          // For medium datasets (40-100), show more labels but still limit
          // For small datasets (<40), show all labels
          maxTicksLimit: isLargeDataset
            ? 12
            : (multiLine ? multiLine.labels.length : data.length) > 40
            ? 20
            : undefined,
          // Auto-skip labels if they overlap
          autoSkip: true,
          // Rotate labels if needed for better readability
          maxRotation: 45,
          minRotation: 0,
        },
        border: {
          display: false,
        },
      },
    },
  };

  return title ? (
    <div className={styles.chartContainer}>
      <div className={styles.chartHeader}>
        <h3 className={styles.chartTitle}>{title}</h3>
        {subtitle && <div className={styles.chartSubtitle}>{subtitle}</div>}
      </div>
      <div className={styles.chartWrapper}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  ) : (
    // When no title, just render chart without container wrapper (used in custom layouts like Total Revenue section)
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <Line data={chartData} options={options} />
    </div>
  );
};

export default LineChart;
